import { useCallback } from 'react';
import { history, useIntl } from 'umi';
import { notification } from 'tea-component';

import { normalizeDatalabRequestError } from '@/pages/datalab/pages/Number/ModelManagementCenter/utils/normalizeDatalabRequestError';

import {
  publishYamlConfigs,
  queryYamlConfigTest,
  testYamlConfigs,
} from '../../../service';
import type {
  IStepYamlDraftState,
  IYamlQueryTestResultData,
  IYamlWorkflow,
  IYamlWorkflowRowFailure,
  IYamlWorkflowRowState,
  TYamlWorkflowOnChange,
} from '../../../types';

import type { TYamlBatchResponse, TYamlQueryTestApiEnvelope } from '../types';

interface IUseStepTestPublishFlowParams {
  gameCode: string;
  cubeNameSummary: string;
  hasTestPrivilege: boolean;
  hasPublishPrivilege: boolean;
  yamlWorkflow: IYamlWorkflow;
  yamlDraft: IStepYamlDraftState;
  onYamlWorkflowChange: TYamlWorkflowOnChange;
}

interface IUseStepTestPublishFlowResult {
  runTests: () => void;
  handlePublish: () => Promise<void>;
}

/**
 * Step4 测试/发布流程：
 * 1. testYamlConfigs（YAML 语法）→ 2. queryYamlConfigTest（Sample SQL）
 * 3. publishYamlConfigs（全部通过后）
 *
 * 失败详情写入 yamlWorkflow.rows[].failures，由 ResultRows 行内展示（不再弹通知）。
 */
export function useStepTestPublishFlow(
  params: IUseStepTestPublishFlowParams,
): IUseStepTestPublishFlowResult {
  const {
    gameCode,
    cubeNameSummary,
    hasTestPrivilege,
    hasPublishPrivilege,
    yamlWorkflow,
    yamlDraft,
    onYamlWorkflowChange,
  } = params;
  const { testRequestLoading, publishLoading } = yamlWorkflow;
  const { yamlText, yamlLoading, savedConfigUuid } = yamlDraft;
  const { formatMessage } = useIntl();

  /** 组装单行状态；failures / success 供 ResultRows 渲染详情 */
  const buildTestRow = useCallback(
    (
      key: IYamlWorkflowRowState['key'],
      status: IYamlWorkflowRowState['status'],
      extra?: {
        failures?: IYamlWorkflowRowFailure[];
        success?: IYamlQueryTestResultData;
      },
    ): IYamlWorkflowRowState => ({
      key,
      status,
      ...(extra?.failures?.length ? { failures: extra.failures } : {}),
      ...(extra?.success ? { success: extra.success } : {}),
    }),
    [],
  );

  /**
   * 第二阶段：Sample Query（依赖 Step3 已 save 的 uuid）。
   * YAML 行保持 pass；本阶段只更新 sample 行与 testPhase。
   */
  const runQueryTest = useCallback(
    (uuid: string) => {
      queryYamlConfigTest({ uuid })
        .then((queryRes: unknown) => {
          const envelope = queryRes as TYamlQueryTestApiEnvelope;
          const codeOk = envelope.code === 0 || envelope.code === 200;
          const queryBody = envelope.data;

          if (!codeOk || !queryBody) {
            const sampleFailures: IYamlWorkflowRowFailure[] = [
              {
                name: cubeNameSummary,
                msg:
                  (envelope.msg && String(envelope.msg).trim()) ||
                  formatMessage({
                    id: 'dbl_model_management_test_failed',
                  }),
                code: envelope.code,
              },
            ];
            onYamlWorkflowChange({
              rows: [
                buildTestRow('yaml', 'pass'),
                buildTestRow('sample', 'fail', { failures: sampleFailures }),
              ],
              testPhase: 'failed',
            });
            return;
          }

          // 成功：queryBody 含 time_dimension / query_sql / total 等，行内绿色摘要展示
          onYamlWorkflowChange({
            rows: [
              buildTestRow('yaml', 'pass'),
              buildTestRow('sample', 'pass', { success: queryBody }),
            ],
            testPhase: 'passed',
          });
        })
        .catch((err: unknown) => {
          const { msg, code } = normalizeDatalabRequestError(err);
          const sampleFailures: IYamlWorkflowRowFailure[] = [{ msg, code }];
          onYamlWorkflowChange({
            testPhase: 'failed',
            rows: [
              buildTestRow('yaml', 'pass'),
              buildTestRow('sample', 'fail', { failures: sampleFailures }),
            ],
          });
        })
        .finally(() => {
          onYamlWorkflowChange({ testRequestLoading: false });
        });
    },
    [buildTestRow, cubeNameSummary, formatMessage, onYamlWorkflowChange],
  );

  /**
   * 执行全部测试：先 YAML 批量 test，通过后再调 runQueryTest。
   * 前置：Step3 已保存草稿（savedConfigUuid）、有 YAML 文本与测试权限。
   */
  const runTests = useCallback((): void => {
    const yamlReady = Boolean(yamlText?.trim()) && !yamlLoading;
    const anyLoading = testRequestLoading || publishLoading;
    const canStart =
      yamlReady &&
      Boolean(savedConfigUuid?.trim()) &&
      hasTestPrivilege &&
      !anyLoading;

    if (!canStart) {
      return;
    }
    const uuid = String(savedConfigUuid).trim();
    if (!uuid) {
      return;
    }

    onYamlWorkflowChange({
      testRequestLoading: true,
      testPhase: 'running',
      rows: [
        buildTestRow('yaml', 'running'),
        buildTestRow('sample', 'pending'),
      ],
    });

    testYamlConfigs({
      items: [{ uuid }],
    })
      .then((testRes: unknown) => {
        const testFirst = ((testRes as TYamlBatchResponse).results || [])[0];
        // YAML 失败：sample 仍为 pending（未执行），勿标 fail
        if (!testFirst || testFirst.code !== 0) {
          const yamlFailures: IYamlWorkflowRowFailure[] = testFirst
            ? [
              {
                uuid: String(testFirst.uuid ?? uuid),
                name: cubeNameSummary,
                msg: testFirst.msg,
                code: testFirst.code,
              },
            ]
            : [
              {
                msg: formatMessage({
                  id: 'dbl_model_management_test_failed',
                }),
              },
            ];
          onYamlWorkflowChange({
            rows: [
              buildTestRow('yaml', 'fail', { failures: yamlFailures }),
              buildTestRow('sample', 'pending'),
            ],
            testPhase: 'failed',
            testRequestLoading: false,
          });
          return;
        }
        // YAML 通过后再跑 Sample；loading 由 runQueryTest.finally 结束
        onYamlWorkflowChange({
          rows: [
            buildTestRow('yaml', 'pass'),
            buildTestRow('sample', 'running'),
          ],
        });
        runQueryTest(uuid);
      })
      .catch((err: unknown) => {
        const { msg, code } = normalizeDatalabRequestError(err);
        const yamlFailures: IYamlWorkflowRowFailure[] = [{ msg, code }];
        onYamlWorkflowChange({
          testPhase: 'failed',
          rows: [
            buildTestRow('yaml', 'fail', { failures: yamlFailures }),
            buildTestRow('sample', 'pending'),
          ],
          testRequestLoading: false,
        });
      });
  }, [
    cubeNameSummary,
    formatMessage,
    hasTestPrivilege,
    onYamlWorkflowChange,
    publishLoading,
    buildTestRow,
    runQueryTest,
    savedConfigUuid,
    testRequestLoading,
    yamlLoading,
    yamlText,
  ]);

  /** 全部测试通过后发布；成功跳转模型管理中心列表 */
  const handlePublish = useCallback(async (): Promise<void> => {
    const testsPassedUi =
      yamlWorkflow.testPhase === 'passed' && Boolean(savedConfigUuid);
    if (
      !testsPassedUi ||
      !savedConfigUuid ||
      publishLoading ||
      !hasPublishPrivilege
    ) {
      return;
    }
    onYamlWorkflowChange({ publishLoading: true });
    try {
      const res = await publishYamlConfigs({
        items: [{ uuid: savedConfigUuid }],
      });
      const first = (res as TYamlBatchResponse).results?.[0];
      if (!first || first.code !== 0) {
        notification.error({
          description: formatMessage(
            { id: 'dbl_model_management_create_model_publish_failed' },
            { name: cubeNameSummary },
          ),
        });
        return;
      }
      notification.success({
        description: formatMessage(
          { id: 'dbl_model_management_create_model_publish_success' },
          { name: cubeNameSummary },
        ),
      });
      history.push(
        `/datalab/${gameCode}/DataManagement/ModelManagementCenter`,
      );
    } catch {
      notification.error({
        description: formatMessage(
          { id: 'dbl_model_management_create_model_publish_failed' },
          { name: cubeNameSummary },
        ),
      });
    } finally {
      onYamlWorkflowChange({ publishLoading: false });
    }
  }, [
    cubeNameSummary,
    formatMessage,
    gameCode,
    hasPublishPrivilege,
    onYamlWorkflowChange,
    publishLoading,
    savedConfigUuid,
    yamlWorkflow.testPhase,
  ]);

  return { runTests, handlePublish };
}
